import type { ThemeColor, FontMode, ThemeSize, WidthMode } from './theme';

// Backend models

// Table-stakes user properties shared across different component contexts
export interface UserCore {
  id: string;
  username: string;
  email: string;
  isSubscriber: boolean;
  formKey: string;
  rssKey: string;
  validStudentDiscount?: boolean;
  display_name?: string;
  profile_picture_url?: string;
  kindleEmail?: string; // Instapaper inbound Kindle address, e.g. kindle.xxxxx@instapaper.com
  emailHashKey?: string; // builds the save-by-email address: readlater.<hash>@instapaper.com
}

export interface User extends UserCore {
  counts: {
    unread: number;
    liked: number;
    archived: number;
    notes: number;
  };
  folders?: Folder[];
  tags: Tag[];
  folderSort?: SidebarFolderSortMode;
  tagSort?: SidebarTagSortMode;
}

export interface Bookmark {
  id: string;
  title: string;
  url: string;
  description?: string;
  progress: number;
  progressTimestamp: number;
  liked: boolean;
  hash: string;
  time: number;
  reading_time: number;
  tags?: Tag[];
  is_archived?: boolean;
  site_name?: string;
  estimated_time?: string;
  notes?: Note[];
  author?: string;
  pubtime?: string;
  estimated_time_remaining?: number;
  estimated_total_time?: number;
  og_image?: string;
  private_source?: string | null;
}

export interface UserRawResponse extends Omit<User, 'folders'> {
  folders: FolderRawResponse[];
  rss_key: string;
  folder_sort?: number;
  tag_sort?: number;
  kindle_email?: string;
  email_hash_key?: string;
}

export interface Folder {
  id: string;
  title: string;
  count: number;
  displayTitle: string; // camelCase transform
  syncToMobile: boolean; // camelCase, type transform
  public: boolean;
  position: number;
  slug: string;
}

export interface FolderRawResponse
  extends Omit<Folder, 'displayTitle' | 'syncToMobile' | 'public'> {
  display_title: number;
  sync_to_mobile: number;
  public: number;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
  count: number;
}

export interface Highlight {
  id: string;
  text: string;
  note?: string;
  position: number;
  apiId?: string;
  bookmarkId?: string;
  created_at?: string;
}

export interface Note {
  id: string;
  text: string;
  note?: string;
  bookmark_id: string;
  time: number;
  position: number;
  highlight_id?: string;
  highlights_this_month?: boolean;
  bookmark_title?: string;
  bookmark_url?: string;
  bookmark_time?: number;
}

// UI types
export type ViewMode =
  | 'home'
  | 'unread'
  | 'archive'
  | 'liked'
  | 'folder'
  | 'videos'
  | 'tags'
  | 'highlights';

export type BookmarkActionViewMode = 'home' | 'archive' | 'liked' | 'folder';
export type SidebarFolderSortMode =
  | 'manual'
  | 'name'
  | 'oldest'
  | 'newest'
  | 'count'
  | 'recently_used';

export type SidebarTagSortMode = Exclude<SidebarFolderSortMode, 'manual'>;

/** Layout for list+reader: 2col (list only), 3col (list + reader), or full (reader only) */
export type ReaderLayout = '2col' | '3col' | 'full';

export interface UserPreferences {
  bookmarkList: {
    view: 'condensed' | 'standard';
    thumbnails: boolean;
    sortOrder: 'newest' | 'oldest';
    columnWidth?: number; // Width in pixels for the bookmark list column (null = CSS default); reader column takes the rest
  };
  reader?: {
    bookmarkId?: string | null; // Last opened bookmark id in the reader column; persists across route changes
    layout?: ReaderLayout; // 2col = list only; full = reader only; 3col = list + reader
  };
  premiumNotice?: {
    dismissedAt?: number; // timestamp when dismissed, auto-resets after 30 days
  };
  theme?: {
    color?: ThemeColor;
    font?: FontMode;
    size?: ThemeSize;
    width?: WidthMode;
    wpm?: number;
  };
  sidebar?: {
    sections: { [key: string]: boolean };
  };
}

// Action parameters
export interface BookmarkActionParams {
  bookmarkId: string;
}

export interface MoveToFolderParams extends BookmarkActionParams {
  folderId: string;
}

export interface TagActionParams extends BookmarkActionParams {
  addTags?: string[];
  removeTags?: string[];
}

type BookmarkProblem =
  | 'clutter'
  | 'missing-text'
  | 'missing-pages'
  | 'missing-images'
  | 'missing-footnotes'
  | 'login-required';

export interface ReportProblemParams extends BookmarkActionParams {
  problem: BookmarkProblem;
}

// Bookmark action types
export type BookmarkActionType =
  | 'like'
  | 'unlike'
  | 'archive'
  | 'home'
  | 'delete'
  | 'move'
  | 'tags'
  | 'report_problem';

export interface BookmarkAction {
  bookmark_id: string;
  action: BookmarkActionType;
  folder_id?: string;
  add_tags?: Array<{ name: string }>;
  remove_tags?: Array<{ name: string }>;
  problem?: BookmarkProblem;
}

export interface BookmarkActionsRequest {
  actions: BookmarkAction[];
}

/** GET /data/bookmarks and section_action `section` object (snake_case for JSON). */
export type BookmarkApiSectionType =
  | 'home'
  | 'liked'
  | 'archive'
  | 'folder'
  | 'tag'
  | 'videos';

export interface BookmarkSectionActionSection {
  section_type: BookmarkApiSectionType;
  folder_id?: string;
  tag?: string;
  sort: 'newest' | 'oldest';
}

/** Client-side mirror of `section` when batching a whole list. */
export type BookmarkSectionBatchTarget = BookmarkSectionActionSection;

/** Action template for POST /data/sections/actions (no bookmark_id; `action_type`
 *  mirrors the section's `section_type`). */
export interface BookmarkSectionActionTemplate {
  action_type: BookmarkActionType;
  folder_id?: string;
  add_tags?: Array<{ name: string }>;
  remove_tags?: Array<{ name: string }>;
}

/** Flat body for POST /data/sections/actions. */
export interface BookmarkSectionActionRequestBody {
  section: BookmarkSectionActionSection;
  action: BookmarkSectionActionTemplate;
}

export interface BookmarkSectionActionResponse {
  status?: boolean;
  async?: boolean;
  section_action?: boolean;
}

// API Response types
export interface UserSessionResponse {
  user: UserRawResponse;
}

export interface BookmarkCollectionResponse {
  bookmarks: Bookmark[];
  user?: UserRawResponse;
  page?: number;
  has_more?: boolean;
}

export interface BookmarkResponse {
  bookmark: Bookmark;
  user?: UserRawResponse;
}

export interface BookmarkContentResponse {
  body: string;
  text?: string;
  pubtime?: string;
  first_text?: string;
  url?: string;
  source_title?: string;
  author?: string;
  is_private_content?: boolean;
  title?: string;
  parser?: string;
  key_sentences?: string[];
  summary?: string;
  paywalled?: boolean;
  direction?: string;
}

// API Error response
export interface ApiErrorResponse {
  message?: string;
  error?: {
    code?: number;
    message?: string;
  };
}

// Transformation utilities
export function transformRawUser(rawUser: UserRawResponse): User {
  // Try multiple methods to get formKey
  let formKey = '';

  // Method 1: Try to get from cookies
  const cookieFormKey = document.cookie
    .split('; ')
    .find((row) => row.startsWith('form_key='))
    ?.split('=')[1];

  if (cookieFormKey) {
    formKey = cookieFormKey;
  }

  // Method 2: Try to get from rawUser.formKey (if it exists and is not empty)
  if (!formKey && rawUser.formKey && rawUser.formKey.trim() !== '') {
    formKey = rawUser.formKey;
  }

  // Method 3: Fallback to rawUser.form_key (snake_case version)
  const rawUserWithFormKey = rawUser as UserRawResponse & {
    form_key?: string;
    is_subscriber?: boolean;
    premium?: boolean;
    valid_student_discount?: boolean;
  };
  if (
    !formKey &&
    rawUserWithFormKey.form_key &&
    rawUserWithFormKey.form_key.trim() !== ''
  ) {
    formKey = rawUserWithFormKey.form_key;
  }

  // Transform premium/is_subscriber to isSubscriber
  // Backend uses 'premium' field
  const isSubscriber =
    rawUser.isSubscriber ??
    rawUserWithFormKey.premium ??
    rawUserWithFormKey.is_subscriber ??
    false;

  // Transform valid_student_discount
  const validStudentDiscount =
    rawUser.validStudentDiscount ?? rawUserWithFormKey.valid_student_discount ?? false;
  const folderSort = normalizeFolderSortMode(rawUser.folder_sort);
  const tagSort = normalizeTagSortMode(rawUser.tag_sort);

  return {
    ...rawUser,
    formKey,
    isSubscriber,
    validStudentDiscount,
    counts: {
      unread: rawUser.counts?.unread ?? 0,
      liked: rawUser.counts?.liked ?? 0,
      archived: rawUser.counts?.archived ?? 0,
      notes: rawUser.counts?.notes ?? 0,
    },
    folders: rawUser.folders?.map(transformRawFolder) ?? [],
    tags: rawUser.tags ?? [],
    rssKey: rawUser.rss_key,
    kindleEmail: rawUser.kindle_email,
    emailHashKey: rawUser.email_hash_key,
    folderSort,
    tagSort,
  };
}

function normalizeFolderSortMode(folderSort?: number): SidebarFolderSortMode {
  switch (folderSort) {
    case 1:
      return 'name';
    case 2:
      return 'oldest';
    case 3:
      return 'newest';
    case 4:
      return 'count';
    case 5:
      return 'recently_used';
    case 0:
    default:
      return 'manual';
  }
}

function normalizeTagSortMode(tagSort?: number): SidebarTagSortMode {
  switch (tagSort) {
    case 2:
      return 'oldest';
    case 3:
      return 'newest';
    case 4:
      return 'count';
    case 5:
      return 'recently_used';
    case 1:
    default:
      return 'name';
  }
}

function transformRawFolder(rawFolder: FolderRawResponse): Folder {
  return {
    id: rawFolder.id,
    title: rawFolder.title,
    count: rawFolder.count,
    displayTitle: rawFolder.display_title.toString(),
    syncToMobile: rawFolder.sync_to_mobile === 1,
    public: rawFolder.public === 1,
    position: rawFolder.position,
    slug: rawFolder.slug,
  };
}

const viewModeToBookmarkActionModeMap: Partial<Record<ViewMode, BookmarkActionViewMode>> =
  {
    home: 'home',
    unread: 'home',
    archive: 'archive',
    liked: 'liked',
    folder: 'folder',
  };

export function viewModeToActionMode(viewMode: ViewMode): BookmarkActionViewMode {
  return viewModeToBookmarkActionModeMap[viewMode] ?? 'home';
}

// Profile types
export interface ProfileUser extends UserCore {
  display_name?: string;
  profile_picture_url?: string;
  joined_date?: string;
  stats?: {
    followers?: number;
    following?: number;
    bookmarks?: number;
  };
}

export interface ProfileData {
  user: ProfileUser;
  bookmarks: Bookmark[];
  next_page?: boolean;
}

export interface HighlightsResponse {
  notes: Note[];
  user?: UserRawResponse;
}

export interface KeySentence {
  id: string;
  text: string;
}
